import parse from 'json-templates';
import { useEffect, useState } from 'react';
import { apiClient } from '../adapters/api-client';
import {
  Consents,
  Context,
  Housing,
  Loans,
  ScoreResponse,
} from '../adapters/api-client/subscription/Subscription.model';
import { FIELDS_ERROR, ModalStatus, TRACKER_TYPE } from '../constants/constants';
import { getQueryParams } from '../utils/QueryParamsUtils';
import { WebAnalysePageModel } from '../utils/Steps';
import webAnalyseModel from '../webAnalyse.config.json';

type TrackerHTMLInputElement = HTMLInputElement & { status: string; value: string; type: string };

type FieldStatus = {
  status: string;
  error: FIELDS_ERROR;
};

type CustomBlurEvent = Event & {
  target: EventTarget & {
    status: string;
    label: string;
    value: string;
    tagName: string;
    type: string;
    className: string;
    id: string;
    parentNode: HTMLElement & {
      label: string;
    };
  };
};

type NextStepButtonEvent = Event & {
  target: EventTarget & {
    disabled: boolean;
  };
};

const buildErrorCode = () => {
  let errorCode = '';
  const fieldsToValidate: NodeListOf<TrackerHTMLInputElement> = document.body.querySelectorAll('.to-validate');
  if (fieldsToValidate && fieldsToValidate.length) {
    errorCode = Array.from(fieldsToValidate)
      .map((element: TrackerHTMLInputElement) => {
        return getFieldStatus(element.status == 'invalid', element.value, element.type).error;
      })
      .join('|');
  }
  return errorCode;
};

const getFieldStatus = (invalid: boolean, value: string, type: string): FieldStatus => {
  const status = !invalid ? 'ok' : 'ko';
  const error = !invalid
    ? FIELDS_ERROR.NO_ERROR
    : value
    ? FIELDS_ERROR.INVALID_FORMAT
    : type === 'text'
    ? FIELDS_ERROR.NOT_FILLED
    : FIELDS_ERROR.NOT_SELECTED;
  return { status, error };
};

const getFieldLabel = (label: string): string | undefined => {
  return label !== undefined && label !== null && label !== '' ? label : undefined;
};

export const trackPageP2 = (
  pageDetails?: WebAnalysePageModel,
  context?: Context,
  modalStatus?: ModalStatus,
  geoLoc?: string,
): void => {
  const template = parse(webAnalyseModel.P2);
  const templateVariable = {
    site: geoLoc === 'CTLM' ? 'cetelem' : 'creditmoderne',
    geoLocation: geoLoc === 'CTLM' ? 'metropole' : geoLoc,
    environment: (window as unknown as WindowWithEnv)._env_.appEnv || 'prod',
    pageName: pageDetails?.pageName,
    stepNumber: pageDetails?.stepNumber,
    stepLabel: pageDetails?.stepLabel,
    vendorNumber: '', //context?.retailerNumber,
  };
  const payload = template(templateVariable);
  if (geoLoc !== 'NONE') {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    (window as any).dataLayer.push(payload);
    //console.log(`sent payload dataLayer P2: ${JSON.stringify(payload)}`);
  }
};

export const trackPageP3 = (
  pageDetails?: WebAnalysePageModel,
  context?: Context,
  scoreResponse?: ScoreResponse,
  housing?: Housing,
  loans?: Loans,
  consents?: Consents,
  geoLoc?: string,
): void => {
  const template = parse(webAnalyseModel.P3);
  const templateVariable = {
    site: geoLoc === 'CTLM' ? 'cetelem' : 'creditmoderne',
    geoLocation: geoLoc === 'CTLM' ? 'metropole' : geoLoc,
    environment: (window as unknown as WindowWithEnv)._env_.appEnv || 'prod',
    pageName: pageDetails?.pageName,
    stepNumber: pageDetails?.stepNumber,
    stepLabel: pageDetails?.stepLabel,
    vendorNumber: context?.retailerNumber || '',
    applicationScore: scoreResponse?.scoreLight == 'V' ? 'Potentiel' : 'Refus',
    housingStatus: housing?.housingCode,
    racProfile: scoreResponse?.customerCategory,
    supportChoice: String(consents?.gdprFlag) === 'true' ? 'oui' : 'non',
    term: scoreResponse?.term || '',
    monthlyPayment: scoreResponse?.creditMonthlyPaymentWithoutInsurance?.toString() || '0',
    amountCreditImmo: loans?.realEstateLoans?.reduce((s, i) => (s += i.amount), 0).toString() || '',
    amountCreditConso: loans?.consumerLoans?.reduce((s, i) => (s += i.amount), 0).toString() || '',
    amountCreditNew: loans?.newProjectAmount.toString() || '',
  };

  const payload = template(templateVariable);
  // eslint-disable  @typescript-eslint/no-explicit-any
  (window as any).dataLayer.push(payload);
  //console.log(`sent payload dataLayer P3: ${JSON.stringify(payload)}`);
};

export const trackPageP4 = (context?: Context, errorCode?: number, geoLoc?: string): void => {
  const template = parse(webAnalyseModel.P4);
  const templateVariable = {
    site: geoLoc === 'CTLM' ? 'cetelem' : 'creditmoderne',
    geoLocation: geoLoc === 'CTLM' ? 'metropole' : geoLoc === 'NONE' ? '' : geoLoc,
    environment: (window as unknown as WindowWithEnv)._env_.appEnv || 'prod',
    typeOfError: errorCode,
    contributorCode: context?.retailerNumber || '',
    creditType: context?.purchaseCode,
  };
  const payload = template(templateVariable);
  // eslint-disable  @typescript-eslint/no-explicit-any
  (window as any).dataLayer.push(payload);
  //console.log(`sent payload dataLayer P4: ${JSON.stringify(payload)}`);
};

//track E4
export const trackE4 = (event: Event): void => {
  const template = parse(webAnalyseModel.E4);
  setTimeout(() => {
    const target = (event as NextStepButtonEvent)?.target;
    // check if the button is inactif
    if (target && target.disabled) {
      const templateVariable = {
        errorCode: buildErrorCode(),
      };
      const payload = template(templateVariable);
      // eslint-disable  @typescript-eslint/no-explicit-any
      (window as any).dataLayer.push(payload);
      //console.log(`sent payload dataLayer E4: ${JSON.stringify(payload)}`);
    }
  }, 500);
};

//track E3
export const trackE3 = (event: Event): void => {
  const template = parse(webAnalyseModel.E3);
  setTimeout(() => {
    const target = (event as CustomBlurEvent)?.target;
    if (target && !['E3-untrackable'].includes(target.className)) {
      //don't process gender-input click (bug with label clickable)
      if (target && !['GENDER-INPUT'].includes(target?.tagName ?? '')) {
        const fieldStatus = getFieldStatus(target.status == 'invalid', target.value, target.type);
        const templateVariable = {
          formInteraction: 'BASE-CHECKBOX' === target?.tagName ? 'Box' : 'champs',
          fieldLabel: getFieldLabel(target.label) ?? getFieldLabel(target.parentNode?.label) ?? target.id,
          fieldStatut: fieldStatus.status,
          //TODO make dynamic value witn coemp case
          stakeHolder: 'Emprunteur 1',
          errorCode: fieldStatus.error.toString(),
        };
        const payload = template(templateVariable);
        // eslint-disable  @typescript-eslint/no-explicit-any
        (window as any).dataLayer.push(payload);
        //console.log(`sent payload dataLayer E3: ${JSON.stringify(payload)}`);
      }
    }
  }, 500);
};

export const trackPageP1 = (pageDetails?: WebAnalysePageModel, context?: Context, geoLoc?: string): void => {
  const template = parse(webAnalyseModel.P1);
  const templateVariable = {
    site: geoLoc === 'CTLM' ? 'cetelem' : 'creditmoderne',
    geoLocation: geoLoc === 'CTLM' ? 'metropole' : geoLoc,
    environment: (window as unknown as WindowWithEnv)._env_.appEnv || 'prod',
    pageName: pageDetails?.pageName,
    stepNumber: pageDetails?.stepNumber,
    stepLabel: pageDetails?.stepLabel,
    vendorNumber: '', //context?.retailerNumber
  };
  const payload = template(templateVariable);
  // eslint-disable  @typescript-eslint/no-explicit-any
  if (geoLoc !== 'NONE') {
    (window as any).dataLayer.push(payload);
    //console.log(`sent payload dataLayer P1: ${JSON.stringify(payload)}`);
  }
};

export const useTracker: (
  trakerTypes: TRACKER_TYPE[],
  pageDetails?: WebAnalysePageModel,
  scoreResponse?: ScoreResponse,
  errorCode?: number,
  modalStatus?: ModalStatus,
  geoLoc?: string,
  housing?: Housing,
  loans?: Loans,
  consents?: Consents,
) => void = (trakerTypes, pageDetails, scoreResponse, errorCode, modalStatus, geoLoc, housing, loans, consents) => {
  const { uuid } = getQueryParams();
  const [context, setContext] = useState<Context>();
  // load and set subscription context
  useEffect(() => {
    const fetchContext = async () => {
      if (!context && uuid) {
        try {
          if (!TRACKER_TYPE.P4) {
            const data = await apiClient().subscription.getContext(uuid);
            setContext(data);
          }
        } catch (err) {
          console.error(err);
        }
      }
    };
    fetchContext();
  }, []);

  useEffect(() => {
    if (geoLoc !== 'NONE') {
      if (trakerTypes) {
        for (let i = 0; i < trakerTypes.length; i++) {
          switch (trakerTypes[i] as TRACKER_TYPE) {
            case TRACKER_TYPE.P1:
              // if (context)
              trackPageP1(pageDetails, context, geoLoc);
              break;
            case TRACKER_TYPE.P2:
              // if (context)
              trackPageP2(pageDetails, context, modalStatus, geoLoc);
              break;
            case TRACKER_TYPE.P3:
              if (scoreResponse) trackPageP3(pageDetails, context, scoreResponse, housing, loans, consents, geoLoc);
              break;
            case TRACKER_TYPE.P4:
              trackPageP4(context, errorCode, geoLoc);
              break;
            case TRACKER_TYPE.E3:
              //champ input
              Array.from(document.body.querySelectorAll('.trackable-blur'))?.forEach((e) => {
                e.addEventListener('input-changed', trackE3, false);
              });
              //element clickable
              Array.from(document.body.querySelectorAll('.trackable-click'))?.forEach((e) => {
                e.addEventListener('click', trackE3, false);
              });
              //element checkbos (ex: consengement)
              Array.from(document.body.querySelectorAll('.trackable-checked'))?.forEach((e) => {
                e.addEventListener('checked-changed', trackE3, false);
              });
              break;
            // case TRACKER_TYPE.E4:
            //   const nextButton = document.querySelector('.trackable-next-step');
            //   nextButton?.addEventListener('click', trackE4, false);
            //   break;
          }
        }
      }
    }
  }, [pageDetails, context, scoreResponse, geoLoc]);

  //E3 inside modals (addEventListner inside modal when modalStatus changes ==> modal is rendered)
  useEffect(() => {
    if (trakerTypes) {
      for (let i = 0; i < trakerTypes.length; i++) {
        switch (trakerTypes[i] as TRACKER_TYPE) {
          case TRACKER_TYPE.E3:
            //element combo-input - add listner on selected-list event
            Array.from(document.body.querySelectorAll('.trackable-list-click-in-modal'))?.forEach((e) => {
              e.addEventListener('selected-list', trackE3, false);
            });
            Array.from(document.body.querySelectorAll('.trackable-gender-input-click-in-modal'))?.forEach((e) => {
              e.addEventListener('click', trackE3, false);
            });
            break;
        }
      }
    }
  }, [modalStatus]);
};
